import { useCallback } from 'react';
import { MeasuringStrategy, type UniqueIdentifier } from '@dnd-kit/core';
import {
  arrayMove,
  defaultAnimateLayoutChanges,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import type { AnimateLayoutChanges } from '@dnd-kit/sortable';
import {
  addWidget,
  createHubDashboard,
  removeWidget,
  selectedHubDashboardSelector,
  updateHubDashboard,
  updateWidget,
  updateWidgetOrder,
  updatedSelectedDashboard,
} from 'slices/hubDashboard.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TWidget } from 'types/hubDashboard.type';
import { Icon, TEInput } from '@timeedit/ui-components';
import { Button, Layout, Row } from '@timeedit/ui-components/antd';
import { Sortable } from '../Components/Sortable/Sortable.component';
import useEditWidgetModal from '../Components/WidgetModal/WidgetModal.component';
import { HubWidgetContainer } from './HubWidgetWrapper';
import './DashboardSettings.scss';

const sortableProps = {
  adjustScale: true,
  Container: HubWidgetContainer,
  strategy: rectSortingStrategy,
};

const DashboardSettings = () => {
  const selectedDashboard = useAppSelector(selectedHubDashboardSelector);
  const dispatch = useAppDispatch();

  const onSubmit = useCallback(
    ({ index, widget }: { index?: number; widget: TWidget }) => {
      if (index !== undefined) {
        dispatch(updateWidget({ index, data: widget }));
      } else {
        dispatch(addWidget(widget));
      }
    },
    [dispatch]
  );

  const { setOpen, setModalType, setWidget, RenderModal } = useEditWidgetModal({
    onSubmit: onSubmit,
  });

  const animateLayoutChanges: AnimateLayoutChanges = (args) =>
    defaultAnimateLayoutChanges({ ...args, wasDragging: true });

  const onReOrderWidget = useCallback(
    (activeIndex: number, overIndex: number) => {
      arrayMove(selectedDashboard?.widgets ?? [], activeIndex, overIndex);
      dispatch(updateWidgetOrder({ activeIndex, overIndex }));
    },
    [dispatch, selectedDashboard]
  );

  const onRemove = useCallback(
    ({ index }: { widgetId: UniqueIdentifier; index: number }) => {
      dispatch(removeWidget(index));
    },
    [dispatch]
  );

  const onEdit = useCallback(
    ({ index }: { widgetId: UniqueIdentifier; index: number }) => {
      setWidget({ index, widget: selectedDashboard?.widgets[index] });
      setModalType('edit');
      setOpen();
    },
    [dispatch, selectedDashboard]
  );

  const onNewWidget = useCallback(() => {
    setWidget(undefined);
    setModalType('add');
    setOpen();
  }, [dispatch, selectedDashboard]);

  const onSubmitDashboard = useCallback(() => {
    if (!selectedDashboard) return;
    if (selectedDashboard.id === 'new') {
      dispatch(createHubDashboard());
    } else {
      dispatch(updateHubDashboard());
    }
  }, [dispatch, selectedDashboard]);

  const onUpdateDashboard = useCallback(
    (value: Record<string, string | string[]>) => {
      if (!selectedDashboard) return;

      dispatch(updatedSelectedDashboard(value));
    },
    [dispatch, selectedDashboard]
  );

  return (
    <Layout
      className="inner-content--wrapper allow-scroll"
      style={{ background: 'unset' }}
    >
      <div className="dashboard-header">
        <div className="dashboard-header-info">
          <span className="dashboard-title">
            <TEInput.EditableTextInput
              value={
                selectedDashboard?.name !== ''
                  ? selectedDashboard?.name
                  : '(New Dashboard)'
              }
              size="large"
              onConfirm={(value) => onUpdateDashboard({ name: value })}
            />
          </span>
          <span className="dashboard-roles">
            <TEInput.EditableTextInput
              value={
                selectedDashboard?.roles?.length
                  ? selectedDashboard?.roles.join(',')
                  : '(No Roles)'
              }
              size="large"
              onConfirm={(value) => {
                const sanitizedRoles = value
                  .split(',')
                  .map((role) => role.trim())
                  .filter((role) => role !== '');
                onUpdateDashboard({ roles: sanitizedRoles });
              }}
            />
          </span>
        </div>
        <div className="dashboard-header-actions">
          <Button type="dashed" onClick={onNewWidget}>
            <Icon id="PlusOutlined" alt="Add" /> Add widget
          </Button>
          <Button type="primary" onClick={onSubmitDashboard}>
            Save
          </Button>
        </div>
      </div>
      <Row
        wrap={true}
        style={{ paddingRight: 15, paddingLeft: 5, paddingBottom: 20 }}
      >
        <Sortable
          {...sortableProps}
          onRemove={onRemove}
          onEdit={onEdit}
          reorderItems={onReOrderWidget}
          items={selectedDashboard?.widgets ?? []}
          animateLayoutChanges={animateLayoutChanges}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        />
      </Row>
      <RenderModal />
    </Layout>
  );
};

export default DashboardSettings;
